<template>
  <div class="relative">
    <div class="relative">
      <input
        v-model="searchQuery"
        @focus="isOpen = true"
        @blur="handleBlur"
        @keydown.enter="selectFirstResult"
        @keydown.arrow-down="highlightNext"
        @keydown.arrow-up="highlightPrevious"
        @keydown.escape="isOpen = false"
        :disabled="disabled"
        type="text"
        placeholder="Search symbols..."
        class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      />
      <svg
        class="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
      </svg>
    </div>

    <!-- Dropdown -->
    <div
      v-if="isOpen && (searchResults.length > 0 || isLoading)"
      class="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md"
    >
      <!-- Loading state -->
      <div v-if="isLoading" class="p-2 text-center text-sm text-muted-foreground">
        <div class="flex items-center justify-center">
          <svg
            class="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Searching...
        </div>
      </div>

      <!-- Results -->
      <div v-else-if="searchResults.length > 0">
        <div
          v-for="(result, index) in searchResults"
          :key="result.symbol"
          @click="selectResult(result)"
          @mouseenter="highlightedIndex = index"
          :class="[
            'cursor-pointer px-3 py-2 text-sm transition-colors',
            highlightedIndex === index
              ? 'bg-accent text-accent-foreground'
              : 'hover:bg-accent hover:text-accent-foreground',
          ]"
        >
          <div class="flex items-center justify-between">
            <span class="font-medium">{{ result.symbol }}</span>
            <span class="text-xs text-muted-foreground">{{ result.list_type }}</span>
          </div>
        </div>
      </div>

      <!-- No results -->
      <div
        v-else-if="searchQuery && !isLoading"
        class="p-2 text-center text-sm text-muted-foreground"
      >
        No symbols found
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { apiClient, type SymbolSearchItem } from '@/lib/api'
import { useDebounceFn } from '@vueuse/core'

interface Props {
  modelValue?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  disabled: false
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const searchQuery = ref(props.modelValue)
const searchResults = ref<SymbolSearchItem[]>([])
const isLoading = ref(false)
const isOpen = ref(false)
const highlightedIndex = ref(-1)

const debouncedSearch = useDebounceFn(async (query: string) => {
  if (!query.trim()) {
    searchResults.value = []
    return
  }

  isLoading.value = true
  try {
    const result = await apiClient.searchSymbols(query, 10)
    searchResults.value = result.results
  } catch (error) {
    console.error('Search error:', error)
    searchResults.value = []
  } finally {
    isLoading.value = false
  }
}, 300)

const selectResult = (result: SymbolSearchItem) => {
  searchQuery.value = result.symbol
  emit('update:modelValue', result.symbol)
  isOpen.value = false
  highlightedIndex.value = -1
}

const selectFirstResult = () => {
  if (searchResults.value.length > 0) {
    selectResult(searchResults.value[0])
  } else if (searchQuery.value.trim()) {
    // If no results but there's a query, emit the user's input
    emit('update:modelValue', searchQuery.value.trim())
    isOpen.value = false
  }
}

const highlightNext = () => {
  if (highlightedIndex.value < searchResults.value.length - 1) {
    highlightedIndex.value++
  }
}

const highlightPrevious = () => {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--
  }
}

const handleBlur = () => {
  // Delay closing to allow click events to fire
  setTimeout(() => {
    isOpen.value = false
    highlightedIndex.value = -1

    // If there's a search query but it wasn't selected, emit it when losing focus
    if (searchQuery.value.trim() && searchQuery.value !== props.modelValue) {
      emit('update:modelValue', searchQuery.value.trim())
    }
  }, 150)
}

// Watch for search query changes - but only trigger search, don't emit until selection
watch(searchQuery, (newQuery) => {
  if (isOpen.value) {
    debouncedSearch(newQuery)
  }
})

// Watch for model value changes from parent
watch(() => props.modelValue, (newValue) => {
  searchQuery.value = newValue
})

// Watch for open state to trigger search
watch(isOpen, (isOpen) => {
  if (isOpen && searchQuery.value) {
    debouncedSearch(searchQuery.value)
  }
})
</script>
